import { useMemo } from 'react'
import { paidPlanTierName } from '@highlight/common'
import { useUnifiedBilling } from '../store/global-billing'
import {
  calculateDaysRemainingFromDate,
  getPlanType,
  formatRenewalDate,
  formatCancellationEndDate,
  formatShortDate,
} from '../utils/billingUtils'

/**
 * Hook that provides commonly used derived values from subscription and paywall state
 * Consolidates repetitive calculations used across components
 */
export const useEnrichedBilling = () => {
  // Read from GlobalStore instead of fetching directly
  const { subscription, billingProfile, paywall: paywallState } = useUnifiedBilling()

  const planType = useMemo(() => getPlanType(subscription.plan), [subscription.plan])

  const hasReceivedTrial = useMemo(() => billingProfile?.hasReceivedTrial ?? false, [billingProfile])

  const daysRemaining = useMemo(() => {
    if (subscription.currentPeriodEndAt) {
      return subscription.startedAt ? calculateDaysRemainingFromDate(subscription.currentPeriodEndAt) : null
    }
    return null
  }, [subscription.currentPeriodEndAt, subscription.startedAt])

  const trialDaysRemaining = useMemo(() => {
    if (paywallState.isTrial && paywallState.trialEndsAt) {
      return calculateDaysRemainingFromDate(paywallState.trialEndsAt)
    }
    return null
  }, [paywallState.isTrial, paywallState.trialEndsAt])

  const renewalDateText = useMemo(() => {
    if (subscription.cancelled) return null
    const scheduled = subscription.isTrial ? null : subscription.scheduledPlan
    const scheduledName = paidPlanTierName(scheduled?.plan)
    if (scheduled && scheduledName) {
      return `Changes to ${scheduledName} on ${formatShortDate(scheduled.startsAt)}`
    }
    if (!subscription.currentPeriodEndAt) return null
    return formatRenewalDate(subscription.currentPeriodEndAt, 'short')
  }, [subscription.currentPeriodEndAt, subscription.cancelled, subscription.isTrial, subscription.scheduledPlan])

  const cancellationEndDateText = useMemo(() => {
    if (!subscription.cancelled || !subscription.currentPeriodEndAt) return null
    return formatCancellationEndDate(subscription.currentPeriodEndAt)
  }, [subscription.cancelled, subscription.currentPeriodEndAt])

  const isFree = useMemo(
    () => !subscription.active && !subscription.isTrial,
    [subscription.active, subscription.isTrial],
  )

  return {
    // Plan type flags
    ...planType,
    isFree,
    // Trial status
    hasReceivedTrial,
    // Days calculations
    daysRemaining,
    trialDaysRemaining,
    // Formatted dates
    renewalDateText,
    cancellationEndDateText,
    // Raw data (for cases where components need it)
    subscription,
    billingProfile,
    paywallState,
  }
}
